import React, { createContext, useContext, useRef, useCallback, useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../context/OrganizationContext';

const ApprovalWSContext = createContext(null);

const WS_RECONNECT_BASE = 1000;
const WS_RECONNECT_MAX = 30000;
const WS_PING_INTERVAL = 30000;
const AUTHORIZED_ROLES = ['admin', 'primary_admin', 'operator', 'owner'];

export function ApprovalWSProvider({ children }) {
  const { user, authReady } = useAuth();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const userRole = user?.role;

  const wsRef = useRef(null);
  const listenersRef = useRef({});
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const [isConnected, setIsConnected] = useState(false);

  const isAuthorized = authReady && !!user && !!orgId && AUTHORIZED_ROLES.includes(userRole);

  const emit = useCallback((eventType, data) => {
    const handlers = listenersRef.current[eventType];
    if (handlers) {
      handlers.forEach((fn) => {
        try { fn(data); } catch (e) { console.warn('[ApprovalWS] Handler error:', e); }
      });
    }
    // Wildcard listeners get all events
    const wildcardHandlers = listenersRef.current['*'];
    if (wildcardHandlers) {
      wildcardHandlers.forEach((fn) => {
        try { fn(data); } catch (e) {}
      });
    }
  }, []);

  const connect = useCallback(() => {
    if (!isAuthorized || !mountedRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    // Close existing connection if in connecting state
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      wsRef.current.close();
    }

    const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/api/ws/approvals?org_id=${encodeURIComponent(orgId)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        console.log('[ApprovalWS] Connected');
        setIsConnected(true);
        reconnectAttemptRef.current = 0;

        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connected') {
            console.log('[ApprovalWS] Server confirmed:', data.role);
            return;
          }
          if (data.type === 'error') {
            console.warn('[ApprovalWS] Server error:', data.message);
            return;
          }
          if (data.type) {
            emit(data.type, data);
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      ws.onclose = (e) => {
        if (!mountedRef.current) return;
        console.log(`[ApprovalWS] Disconnected: code=${e.code}`);
        setIsConnected(false);
        wsRef.current = null;
        clearInterval(pingTimerRef.current);

        // Don't reconnect on auth/permission failures
        if (e.code === 4001 || e.code === 4003 || e.code === 4000) {
          console.log('[ApprovalWS] Not reconnecting (auth/permission issue)');
          return;
        }

        // Exponential backoff reconnect
        const delay = Math.min(
          WS_RECONNECT_BASE * Math.pow(2, reconnectAttemptRef.current),
          WS_RECONNECT_MAX
        );
        reconnectAttemptRef.current++;
        console.log(`[ApprovalWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current})`);
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onclose handles cleanup + reconnect
      };
    } catch (e) {
      console.warn('[ApprovalWS] Connection failed:', e);
    }
  }, [isAuthorized, orgId, emit]);

  // Connect/disconnect based on auth state
  useEffect(() => {
    mountedRef.current = true;

    if (isAuthorized) {
      connect();
    } else {
      // Disconnect if no longer authorized
      if (wsRef.current) {
        wsRef.current.close(1000, 'deauth');
        wsRef.current = null;
      }
      setIsConnected(false);
    }

    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(pingTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close(1000, 'cleanup');
        wsRef.current = null;
      }
    };
  }, [isAuthorized, orgId, connect]);

  // Subscribe to events — returns unsubscribe function
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current[eventType]) {
      listenersRef.current[eventType] = new Set();
    }
    listenersRef.current[eventType].add(callback);
    return () => {
      listenersRef.current[eventType]?.delete(callback);
    };
  }, []);

  const value = React.useMemo(() => ({ subscribe, isConnected }), [subscribe, isConnected]);

  return (
    <ApprovalWSContext.Provider value={value}>
      {children}
    </ApprovalWSContext.Provider>
  );
}

/**
 * Hook to subscribe to approval WebSocket events.
 * @param {string} eventType - Event type to listen for ('approval_created', 'approval_resolved', '*')
 * @param {Function} callback - Called with event data when event fires
 * @returns {{ isConnected: boolean }}
 */
export function useApprovalWS(eventType, callback) {
  const ctx = useContext(ApprovalWSContext);

  useEffect(() => {
    if (!ctx?.subscribe || !callback || !eventType) return;
    return ctx.subscribe(eventType, callback);
  }, [ctx, eventType, callback]);

  return { isConnected: ctx?.isConnected || false };
}

export default ApprovalWSContext;
